// The PNR operation center's airport design tokens — the non-component half of
// the visual language in `airport-ui.tsx` (kept apart so that file exports only
// components, per the react-refresh rule).
//
// Status colour used to be spelled out ad-hoc at every call site
// (`text-emerald-700 dark:text-emerald-400` and friends, ~40 copies). It now
// lives in ONE map keyed by MEANING, so a status reads the same everywhere and a
// re-skin is a single edit. Semantic status colour is deliberately not a brand
// token (frontend-conventions §18.4 governs the brand palette): red means
// disrupted for every airline.

/** What a piece of state MEANS — never what colour it is. */
export type BoardTone = 'ok' | 'warn' | 'alert' | 'info' | 'idle';

export interface ToneSpec {
  /** Foreground for text + icons. */
  text: string;
  /** Soft surface for panels/nodes. */
  surface: string;
  /** Border that pairs with `surface`. */
  border: string;
  /** Solid fill for lamps + rails. */
  solid: string;
  /** Filled chip (surface + text + ring in one). */
  chip: string;
}

const TONE: Record<BoardTone, ToneSpec> = {
  ok: {
    text: 'text-emerald-700 dark:text-emerald-400',
    surface: 'bg-emerald-50 dark:bg-emerald-950/40',
    border: 'border-emerald-500/30 dark:border-emerald-400/25',
    solid: 'bg-emerald-500',
    chip: 'bg-emerald-100 text-emerald-800 ring-emerald-600/20 dark:bg-emerald-900/40 dark:text-emerald-200 dark:ring-emerald-400/20',
  },
  warn: {
    text: 'text-amber-700 dark:text-amber-400',
    surface: 'bg-amber-50 dark:bg-amber-950/40',
    border: 'border-amber-500/30 dark:border-amber-400/25',
    solid: 'bg-amber-500',
    chip: 'bg-amber-100 text-amber-900 ring-amber-600/20 dark:bg-amber-900/40 dark:text-amber-200 dark:ring-amber-400/20',
  },
  alert: {
    text: 'text-rose-600 dark:text-rose-400',
    surface: 'bg-rose-50 dark:bg-rose-950/40',
    border: 'border-rose-500/30 dark:border-rose-400/25',
    solid: 'bg-rose-500',
    chip: 'bg-rose-100 text-rose-800 ring-rose-600/20 dark:bg-rose-900/40 dark:text-rose-200 dark:ring-rose-400/20',
  },
  info: {
    text: 'text-indigo-700 dark:text-indigo-400',
    surface: 'bg-indigo-50 dark:bg-indigo-950/40',
    border: 'border-indigo-500/30 dark:border-indigo-400/25',
    solid: 'bg-indigo-500',
    chip: 'bg-indigo-100 text-indigo-800 ring-indigo-600/20 dark:bg-indigo-900/40 dark:text-indigo-200 dark:ring-indigo-400/20',
  },
  idle: {
    text: 'text-muted-foreground',
    surface: 'bg-muted/50',
    border: 'border-border',
    solid: 'bg-muted-foreground/50',
    chip: 'bg-muted text-muted-foreground ring-border',
  },
};

export function toneClasses(tone: BoardTone): ToneSpec {
  return TONE[tone];
}

/** The engraved micro caption printed over a value on a boarding pass. Use the
 *  `FieldLabel` component where you control the element; this raw class is for
 *  the places that must style someone else's element (e.g. `CardTitle`). */
export const FIELD_LABEL_CLASS =
  'text-[10px] font-semibold uppercase leading-none tracking-[0.14em] text-muted-foreground';

/** The operation center's BOTONERA — ONE visual grammar for the sticky strip:
 *  three segmented GROUPS (PNR · comms · resolution), each a bordered pill whose
 *  cells are identical ghost buttons (`BAR_ACTION_CLASS`; the border belongs to
 *  the group, never the button). Groups never break apart — on a narrow dialog
 *  a whole pill wraps to the next line, so the strip's aspect stays the same at
 *  every width. The one FILLED cell is the contextual primary. */
export const BAR_GROUP_CLASS =
  'flex shrink-0 items-center overflow-hidden rounded-md border border-border bg-background divide-x divide-border';

/** One cell of a botonera group — compact, squared, borderless (see above). */
export const BAR_ACTION_CLASS = 'h-8 rounded-none border-0 px-2.5 text-xs';
